import { Footer } from "@/components/Footer";
import { NewsList } from "@/components/News/NewsList";
import { TableList } from "@/components/Table/TableList";
import { Nav } from "@/components/common/FrontendNav";
import { Option, Select } from "@/components/common/Select";
import { Table } from "@/components/common/Table";
import { getBundesligaNews } from "@/data/news";
import { getActiveSeasons } from "@/data/season";
import prisma from "@/helper/client";
import { LeagueTeam } from "@/models/LeagueTeam";
import { Season } from "@/models/Season";
import { News } from "@/models/types";

import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useRouter } from "next/router";

export async function getServerSideProps(context: any) {
  const { res } = context;
  res.setHeader(
    "Cache-Control",
    "public, max-age=300, stale-while-revalidate=360"
  );
  if (!context.query.season) {
    return {
      redirect: {
        destination: "/bundesliga/table",
        permanent: false,
      },
    };
  }
  const [start, end] = context.query.season.split("-");
  const season = await prisma.season.findFirst({
    where: { startYear: parseInt(start), endYear: parseInt(end) },
  });

  if (!season) {
    return {
      redirect: {
        destination: "/bundesliga/table",
        permanent: false,
      },
    };
  }
  const bundesliga = await prisma.bundesLiga.findFirst({
    where: { seasonId: season.id },
  });

  const teams = await prisma.leagueTeam.findMany({
    where: {
      league: "Bundesliga",
      leagueId: bundesliga?.id,
      DBStatus: "Active",
      seasonTeam: { isActive: "Yes" },
    },
    orderBy: { rank: "asc" },
    include: { seasonTeam: true },
  });
  const { news } = await getBundesligaNews({
    locale: context.locale,
  });

  const seasons = await getActiveSeasons();

  return {
    props: {
      teams,
      news,
      seasons,
      selectedSeason: season,
      ...(await serverSideTranslations(context.locale)),
    },
  };
}

interface PageProps {
  teams: LeagueTeam[];
  news: News[];
  seasons: Season[];
  selectedSeason: Season;
}
export default function Page({
  teams,
  news,
  selectedSeason,
  seasons,
}: PageProps) {
  const { t } = useTranslation();
  const { push } = useRouter();
  const selectedSeasonQuery = `${selectedSeason.startYear}-${selectedSeason.endYear}`;
  return (
    <>
      <Head>
        <title>{`${t("Table_Tennis")} Bundesliga - TTBL - ${t(
          "Title_Table"
        )}`}</title>
      </Head>
      <div>
        <Nav>
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/table/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </Nav>
        <div className="mx-4 mb-4 lg:hidden">
          <Select
            value={selectedSeasonQuery}
            onChange={(e) => push(`/bundesliga/table/${e}`)}
            className="white lg:border-[1px] lg:ml-4"
          >
            {seasons.map((s) => (
              <Option
                key={s.id}
                value={`${s.startYear}-${s.endYear}`}
              >{`${s.startYear}-${s.endYear}`}</Option>
            ))}
          </Select>
        </div>
        <div className="container max-lg:hidden">
          <TableList teams={teams} season={selectedSeason} />
        </div>
        <div className="container container-padding-responsive lg:hidden">
          <Table teams={teams} season={selectedSeason} />
        </div>
        <div className="container">
          <div className="grid grid-cols-[min-content_auto] gap-2 lg:flex lg:flex-row lg:ml-4">
            <div className="w-6 h-4 lg:self-center rounded-sm bg-green" />
            <div className="mr-4">{t("Playoffs")}</div>
            <div className="w-6 h-4 lg:self-center rounded-sm bg-red" />
            <div className="mr-4">{t("Relegation")}</div>
            <div className="font-thin text-gray-text">
              {t("Label_Encounters")}
            </div>
            <div className="mr-4">{t("Encounters")}</div>
            <div className="font-thin text-gray-text">{t("W")}</div>
            <div className="mr-4">{t("Wins")}</div>
            <div className="font-thin text-gray-text">{t("L")}</div>
            <div className="mr-4">{t("Losses")}</div>
            <div className="font-thin text-gray-text">+/-</div>
            <div className="">{t("Difference_between_won_and_lost_games")}</div>
          </div>
        </div>
        {news.length > 0 && (
          <div className="container container_py">
            <NewsList news={news} title="Bundesliga News" type="bundesliga" />
          </div>
        )}
        <Footer />
      </div>
    </>
  );
}
